import React, { useMemo, useRef, useState } from "react";
import CollapsibleTable from "../components/CollapsibleTable";
import { API_BASE_URL } from "../App";
import "../styles/MainPage.css";

function MainPage({ teams = [], setWatching = () => {} }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [genderFilter, setGenderFilter] = useState("");
  const [divisionSuffixFilter, setDivisionSuffixFilter] = useState("");
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [actionStatus, setActionStatus] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [teamPlayersById, setTeamPlayersById] = useState({});
  const [loadingTeams, setLoadingTeams] = useState({});
  const [teamLoadErrors, setTeamLoadErrors] = useState({});
  const teamRefs = useRef({});

  const getDivisionSuffix = (divisionName) => {
    const parts = String(divisionName || "").split("Division");
    return parts[1]?.trim() || "";
  };

  const teamsById = useMemo(() => {
    return teams.reduce((acc, team) => {
      acc[team.TeamXID] = team;
      return acc;
    }, {});
  }, [teams]);

  const filteredTeams = useMemo(() => {
    return teams.filter((team) => {
      const matchesGender = genderFilter ? team.DivisionGender === genderFilter : true;
      const matchesDivisionSuffix = divisionSuffixFilter
        ? getDivisionSuffix(team.DivisionName) === divisionSuffixFilter
        : true;
      const matchesSearch = searchTerm
        ? String(team.TeamName || "").toLowerCase().includes(searchTerm)
        : true;

      return matchesGender && matchesDivisionSuffix && matchesSearch;
    });
  }, [teams, genderFilter, divisionSuffixFilter, searchTerm]);

  const selectedTeam = selectedTeamId ? teamsById[selectedTeamId] : null;

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
  };

  const handleGenderToggle = (gender) => {
    setGenderFilter((prev) => (prev === gender ? "" : gender));
  };

  const handleDivisionSuffixChange = (event) => {
    setDivisionSuffixFilter(event.target.value);
  };

  const handleSelectPlayer = (playerXID) => {
    if (!playerXID) return;
    setSelectedPlayers((prev) =>
      prev.includes(playerXID) ? prev.filter((id) => id !== playerXID) : [...prev, playerXID]
    );
  };

  const handleSelectTeamPlayers = (teamXIDs, isSelected) => {
    setSelectedPlayers((prev) => {
      if (isSelected) {
        return [...new Set([...prev, ...teamXIDs])];
      }
      return prev.filter((id) => !teamXIDs.includes(id));
    });
  };

  const showStatus = (msg, isError = false) => {
    setActionStatus({ msg, isError });
    setTimeout(() => setActionStatus(null), 3000);
  };

  const handleAddToAllTournament = async () => {
    if (selectedPlayers.length === 0) return;

    try {
      await Promise.all(
        selectedPlayers.map((playerXID) =>
          fetch(`${API_BASE_URL}/api/player-details`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ playerXID, allTournament: true }),
          })
        )
      );
      showStatus(`${selectedPlayers.length} player(s) added to All Tournament.`);
      setSelectedPlayers([]);
    } catch (error) {
      console.error("Error updating All Tournament:", error);
      showStatus("Failed to update players.", true);
    }
  };

  const handleAddToTracked = async () => {
    if (selectedPlayers.length === 0) return;

    try {
      await Promise.all(
        selectedPlayers.map((playerXID) =>
          fetch(`${API_BASE_URL}/api/player-details`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ playerXID, tracked: true }),
          })
        )
      );
      showStatus(`${selectedPlayers.length} player(s) added to Tracked List.`);
      if (selectedTeam) {
        setWatching((prev) => {
          const exists = prev.some((team) => team.TeamXID === selectedTeam.TeamXID);
          if (exists) return prev;
          return [
            ...prev,
            {
              TeamXID: selectedTeam.TeamXID,
              TeamName: selectedTeam.TeamName,
              DivisionName: selectedTeam.DivisionName,
              DivisionGender: selectedTeam.DivisionGender,
            },
          ];
        });
      }
      setSelectedPlayers([]);
    } catch (error) {
      console.error("Error updating Tracked:", error);
      showStatus("Failed to update players.", true);
    }
  };

  const handleTeamClick = async (team) => {
    const teamId = team.TeamXID;

    setSelectedTeamId(teamId);
    setSelectedPlayers([]);

    if (teamPlayersById[teamId] || loadingTeams[teamId]) {
      setTimeout(() => {
        teamRefs.current[teamId]?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
      return;
    }

    setLoadingTeams((prev) => ({ ...prev, [teamId]: true }));
    setTeamLoadErrors((prev) => ({ ...prev, [teamId]: null }));

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/team-roster?teamXID=${encodeURIComponent(teamId)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTeamPlayersById((prev) => ({ ...prev, [teamId]: data }));

      setTimeout(() => {
        teamRefs.current[teamId]?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (error) {
      console.error(`Error fetching roster for team ${team.TeamName}:`, error);
      setTeamLoadErrors((prev) => ({
        ...prev,
        [teamId]: "Failed to load this team roster.",
      }));
    } finally {
      setLoadingTeams((prev) => ({ ...prev, [teamId]: false }));
    }
  };

  return (
    <div className="main-page">
      <div className="left-pane">
        <label>Gender</label>
        <div className="gender-filters">
          <button
            className={genderFilter === "M" ? "active" : ""}
            onClick={() => handleGenderToggle("M")}
          >
            Male
          </button>
          <button
            className={genderFilter === "W" ? "active" : ""}
            onClick={() => handleGenderToggle("W")}
          >
            Female
          </button>
        </div>

        <label>Division</label>
        <select value={divisionSuffixFilter} onChange={handleDivisionSuffixChange}>
          <option value="">All</option>
          <option value="I">DI</option>
          <option value="IAA">DIAA</option>
          <option value="IAAA">DIAAA</option>
          <option value="II">DII</option>
          <option value="IIAA">DIIAA</option>
          <option value="IIAAA">DIIAAA</option>
          <option value="III">DIII</option>
          <option value="IIIAA">DIIIAA</option>
        </select>

        <input
          type="text"
          placeholder="Search teams..."
          value={searchTerm}
          onChange={handleSearch}
        />

        <ul>
          {filteredTeams.map((team) => (
            <li
              key={team.TeamXID}
              className={selectedTeamId === team.TeamXID ? "team-loaded" : ""}
              onClick={() => handleTeamClick(team)}
            >
              <span>{team.TeamName}</span>
              <small>{team.DivisionName}</small>
            </li>
          ))}
        </ul>
      </div>

      <div className="right-pane">
        <div className={`action-toolbar ${selectedPlayers.length > 0 ? "toolbar-visible" : ""}`}>
          <span className="selection-count">
            {selectedPlayers.length} player{selectedPlayers.length !== 1 ? "s" : ""} selected
          </span>
          <button
            className="toolbar-btn toolbar-btn-att"
            disabled={selectedPlayers.length === 0}
            onClick={handleAddToAllTournament}
          >
            All Tournament
          </button>
          <button
            className="toolbar-btn toolbar-btn-track"
            disabled={selectedPlayers.length === 0}
            onClick={handleAddToTracked}
          >
            Track
          </button>
          {selectedPlayers.length > 0 && (
            <button className="toolbar-btn toolbar-btn-clear" onClick={() => setSelectedPlayers([])}>
              ✕ Clear
            </button>
          )}
          {actionStatus && (
            <span className={`action-status ${actionStatus.isError ? "status-error" : "status-success"}`}>
              {actionStatus.msg}
            </span>
          )}
        </div>

        {!selectedTeam && (
          <div className="empty-team-state">Select a team from the left to load its roster.</div>
        )}

        {selectedTeam && (() => {
          const teamId = selectedTeam.TeamXID;
          const players = teamPlayersById[teamId] || [];

          return (
            <div key={teamId} ref={(el) => (teamRefs.current[teamId] = el)}>
              {loadingTeams[teamId] ? (
                <div className="team-loading">Loading {selectedTeam.TeamName} roster...</div>
              ) : teamLoadErrors[teamId] ? (
                <div className="team-load-error">{teamLoadErrors[teamId]}</div>
              ) : (
                <CollapsibleTable
                  team={selectedTeam.TeamName}
                  players={players}
                  selectedPlayers={selectedPlayers}
                  onSelectPlayer={handleSelectPlayer}
                  onSelectTeam={handleSelectTeamPlayers}
                />
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default MainPage;
